import type { ReactNode } from 'react'
import { AppShell } from './AppShell'

// Same visual shell as the SSR pages this replaces (pkg/web/templates.go's `page()` +
// `.wrap`), so the transition between the marketing site and the app doesn't jar.
//
// AppShell renders nothing pre-login (auth pages using AuthLayout — none currently do,
// but the guard costs nothing and means this stays correct if one starts to) and nothing
// post-logout, so it's safe to always include here rather than have every page opt in.
export function AuthLayout({ title, children, wide }: { title: string; children: ReactNode; wide?: boolean }) {
  return (
    <div>
      <AppShell />
      <div className={`mx-auto px-6 py-14 ${wide ? 'max-w-6xl' : 'max-w-md'}`}>
        <h1 className="mb-6 text-2xl font-semibold tracking-tight text-ink">{title}</h1>
        {children}
      </div>
    </div>
  )
}

// Spinner: the pages that gate on an in-flight query used to fall back to `return null`
// while loading — a blank page with no feedback that anything is happening at all. Used
// bare for a full-page loading state, or inline (size="sm") next to a busy button's label.
export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const px = size === 'sm' ? 14 : 22
  return (
    <svg
      className="animate-spin text-amber"
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      role="status"
      aria-label="Laden"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

// PageLoading: the standard "still working on it" state for a page gated behind one or
// more queries — replaces the old bare `return null`, which left a visitor staring at an
// empty screen with no signal the app was doing anything.
export function PageLoading({ title }: { title?: string }) {
  return (
    <div>
      <AppShell />
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-14 text-ink-soft">
        <Spinner />
        <span>{title ?? 'Laden…'}</span>
      </div>
    </div>
  )
}

export function ErrorBox({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <div className="mb-4 rounded-lg border border-[#C1666B] bg-[#C1666B22] px-4 py-2.5 text-sm text-ink">
      {children}
    </div>
  )
}

export function NoticeBox({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <div className="mb-4 rounded-lg border border-amber bg-[#E8A33D22] px-4 py-2.5 text-sm text-ink">
      {children}
    </div>
  )
}

export function OkBox({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <div className="mb-4 rounded-lg border border-[#2A9D8F] bg-[#2A9D8F22] px-4 py-2.5 text-sm text-ink">
      {children}
    </div>
  )
}

export function Field({ label, ...props }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-sm text-ink-soft">{label}</span>
      <input
        {...props}
        className="w-full rounded-lg border border-line bg-card px-3 py-2.5 text-base text-ink focus:outline-none focus:ring-2 focus:ring-amber"
      />
    </label>
  )
}

export function SubmitButton({ children, ...props }: { children: ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className="mt-2 w-full rounded-lg bg-amber px-4 py-3 font-semibold text-[#0E2A3B] transition-opacity hover:opacity-90 disabled:opacity-50"
    >
      {children}
    </button>
  )
}
